import {
  isLegalList,
  isLegalSubheading,
  type LegalBlock,
  type LegalDocument,
  type LegalInline,
  type LegalOutlineEntry,
  type LegalText,
  legalOutline,
  parseLegalText,
} from "@serophin/shared";

export type LegalClasses = {
  article?: string;
  header?: string;
  title?: string;
  lead?: string;
  updated?: string;
  notice?: string;
  summary?: string;
  contents?: string;
  contentsTitle?: string;
  contentsList?: string;
  contentsLink?: string;
  section?: string;
  heading?: string;
  subheading?: string;
  paragraph?: string;
  list?: string;
  orderedList?: string;
  link?: string;
  strong?: string;
  closing?: string;
};

type WithClasses = { classes: LegalClasses };

const Inline = ({ part, classes }: WithClasses & { part: LegalInline }) => {
  if (part.kind === "strong") {
    return <strong class={classes.strong}>{part.text}</strong>;
  }
  if (part.kind === "link") {
    return part.external ? (
      <a href={part.href} target="_blank" rel="noopener noreferrer" class={classes.link}>
        {part.text}
      </a>
    ) : (
      <a href={part.href} class={classes.link}>
        {part.text}
      </a>
    );
  }
  return <>{part.text}</>;
};

export const LegalInlineText = ({ text, classes }: WithClasses & { text: LegalText }) => (
  <>
    {parseLegalText(text).map((part, index) => (
      <Inline key={index} part={part} classes={classes} />
    ))}
  </>
);

const Block = ({ block, classes }: WithClasses & { block: LegalBlock }) => {
  if (isLegalSubheading(block)) {
    return (
      <h3 id={block.id} class={classes.subheading}>
        {block.subheading}
      </h3>
    );
  }
  if (isLegalList(block)) {
    const items = block.list.map((item) => (
      <li key={item}>
        <LegalInlineText text={item} classes={classes} />
      </li>
    ));
    return block.ordered ? <ol class={classes.orderedList}>{items}</ol> : <ul class={classes.list}>{items}</ul>;
  }
  return (
    <p class={classes.paragraph}>
      <LegalInlineText text={block} classes={classes} />
    </p>
  );
};

const Section = ({ entry, classes }: WithClasses & { entry: LegalOutlineEntry }) => (
  <section aria-labelledby={entry.id} class={classes.section}>
    <h2 id={entry.id} class={classes.heading}>
      {entry.heading}
    </h2>
    {entry.section.blocks.map((block, index) => (
      <Block key={index} block={block} classes={classes} />
    ))}
  </section>
);

const Contents = ({ outline, classes }: WithClasses & { outline: LegalOutlineEntry[] }) => (
  <nav aria-label="Contents" class={classes.contents}>
    <h2 class={classes.contentsTitle}>Contents</h2>
    <ol class={classes.contentsList}>
      {outline.map((entry) => (
        <li key={entry.id}>
          <a href={`#${entry.id}`} class={classes.contentsLink}>
            {entry.heading}
          </a>
        </li>
      ))}
    </ol>
  </nav>
);

export const LegalHeader = ({ doc, classes }: WithClasses & { doc: LegalDocument }) => (
  <header class={classes.header}>
    <h1 class={classes.title}>{doc.title}</h1>
    {doc.lead && <p class={classes.lead}>{doc.lead}</p>}
    <p class={classes.updated}>Last updated {doc.updated}</p>
  </header>
);

export const LegalBody = ({ doc, classes }: WithClasses & { doc: LegalDocument }) => {
  const outline = legalOutline(doc);
  return (
    <>
      {doc.notice && (
        <p role="note" class={classes.notice}>
          <LegalInlineText text={doc.notice} classes={classes} />
        </p>
      )}
      {doc.summary && (
        <p class={classes.summary}>
          <LegalInlineText text={doc.summary} classes={classes} />
        </p>
      )}
      {doc.contents && <Contents outline={outline} classes={classes} />}
      {outline.map((entry) => (
        <Section key={entry.id} entry={entry} classes={classes} />
      ))}
      {doc.closing?.map((text) => (
        <p key={text} class={classes.closing}>
          <LegalInlineText text={text} classes={classes} />
        </p>
      ))}
    </>
  );
};

export const LegalDocumentView = ({ doc, classes }: WithClasses & { doc: LegalDocument }) => (
  <article class={classes.article}>
    <LegalHeader doc={doc} classes={classes} />
    <LegalBody doc={doc} classes={classes} />
  </article>
);
